import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Newspaper, Plus, Pencil, Trash2, Loader2, RefreshCw, 
  ExternalLink, Clock, Twitter, Instagram, Globe, Eye, EyeOff
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import ImageUpload from "../ImageUpload";
import LlmRegenerateButton from "./LlmRegenerateButton";
import SeoMetaEditor from "./SeoMetaEditor";
import ViewLiveLink from "./ViewLiveLink";

const NewsAdmin = () => {
  const { token } = useAuth();
  const [articles, setArticles] = useState([]);
  const [socialAccounts, setSocialAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraperStatus, setScraperStatus] = useState(null);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState("articles");
  const [filter, setFilter] = useState({ team: "all", category: "all" });
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingSocial, setEditingSocial] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [socialDialogOpen, setSocialDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);

  const emptyArticle = {
    headline: "",
    summary: "",
    long_summary: "",
    source: "",
    source_url: "",
    team: "men",
    category: "latest",
    image_url: ""
  };

  const emptySocial = {
    platform: "twitter",
    handle: "",
    display_name: "",
    is_active: true
  };

  const emptyBlog = {
    title: "",
    content: "",
    author: "",
    images: ["", ""],
    links: [{ url: "", text: "" }, { url: "", text: "" }],
    is_published: true
  };

  useEffect(() => {
    fetchArticles();
    fetchSocialAccounts();
    fetchScraperStatus();
    fetchSectionStatus();
    fetchBlogPosts();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/news/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data.articles || response.data);
    } catch (error) {
      toast.error("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchSocialAccounts = async () => {
    try {
      const response = await axios.get(`${API}/news/social-accounts`);
      const data = response.data;
      // API returns { accounts: { men: [], women: [] }, source: "default" }
      // Flatten into a single array with platform info
      let accounts = [];
      if (data.accounts) {
        if (Array.isArray(data.accounts)) {
          accounts = data.accounts;
        } else {
          // Convert { men: [], women: [] } to flat array
          if (data.accounts.men) {
            accounts = accounts.concat(data.accounts.men.map(a => ({...a, team: 'men', is_active: true, platform: 'twitter'})));
          }
          if (data.accounts.women) {
            accounts = accounts.concat(data.accounts.women.map(a => ({...a, team: 'women', is_active: true, platform: 'twitter'})));
          }
        }
      } else if (Array.isArray(data)) {
        accounts = data;
      }
      setSocialAccounts(accounts);
    } catch (error) {
      console.error("Failed to fetch social accounts");
      setSocialAccounts([]);
    }
  };

  const fetchScraperStatus = async () => {
    try {
      const response = await axios.get(`${API}/news/status`);
      setScraperStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch scraper status");
    }
  };

  const fetchSectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/section-toggles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.news_section_enabled);
    } catch (error) {
      console.error("Failed to fetch section status");
    }
  };

  const toggleSection = async () => {
    setToggling(true);
    try {
      const response = await axios.post(`${API}/admin/section-toggle/news_section_enabled`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.news_section_enabled);
      toast.success(`News section ${response.data.news_section_enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle section");
    } finally {
      setToggling(false);
    }
  };

  const refreshNews = async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API}/news/refresh`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "News refreshed successfully");
      fetchArticles();
      fetchScraperStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh news");
    } finally {
      setRefreshing(false);
    }
  };

  const saveArticle = async () => {
    try {
      if (editingArticle.id) {
        await axios.put(`${API}/news/admin/${editingArticle.id}`, editingArticle, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/news/admin`, editingArticle, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Article created successfully");
      }
      setDialogOpen(false);
      setEditingArticle(null);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save article");
    }
  };

  const deleteArticle = async (id) => {
    if (!confirm("Are you sure you want to delete this article?")) return;
    try {
      await axios.delete(`${API}/news/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Article deleted");
      fetchArticles();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const saveSocialAccount = async () => {
    try {
      if (editingSocial.id) {
        await axios.put(`${API}/news/social-accounts/${editingSocial.id}`, editingSocial, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Social account updated");
      } else {
        await axios.post(`${API}/news/social-accounts`, editingSocial, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Social account added");
      }
      setSocialDialogOpen(false);
      setEditingSocial(null);
      fetchSocialAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save social account");
    }
  };

  const deleteSocialAccount = async (id) => {
    if (!confirm("Are you sure you want to delete this social account?")) return;
    try {
      await axios.delete(`${API}/news/social-accounts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Social account deleted");
      fetchSocialAccounts();
    } catch (error) {
      toast.error("Failed to delete social account");
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blogs/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogPosts(response.data.blogs || []);
    } catch (error) {
      console.error("Failed to fetch blog posts");
    }
  };

  const saveBlog = async () => {
    try {
      const payload = {
        ...editingBlog,
        images: editingBlog.images.filter(i => i && i.trim()),
        links: editingBlog.links.filter(l => l.url && l.url.trim())
      };
      if (editingBlog.id) {
        await axios.put(`${API}/blogs/admin/${editingBlog.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog post updated");
      } else {
        await axios.post(`${API}/blogs/admin`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog post published");
      }
      setBlogDialogOpen(false);
      setEditingBlog(null);
      fetchBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save blog post");
    }
  };

  const deleteBlog = async (id) => {
    if (!confirm("Are you sure you want to delete this blog post?")) return;
    try {
      await axios.delete(`${API}/blogs/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blog post deleted");
      fetchBlogPosts();
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };

  const filteredArticles = articles.filter(a => {
    if (filter.team !== "all" && a.team !== filter.team) return false;
    if (filter.category !== "all" && a.category !== filter.category) return false;
    return true;
  });

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "twitter": return <Twitter className="w-4 h-4" />;
      case "instagram": return <Instagram className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="news-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Newspaper className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          NEWS ADMIN
        </h1>
      </div>

      {/* Section Visibility Toggle */}
      <div className={`rounded-xl p-4 mb-6 border-2 ${sectionEnabled ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {sectionEnabled ? <Eye className="w-6 h-6 text-green-600" /> : <EyeOff className="w-6 h-6 text-red-600" />}
            <div>
              <h3 className="font-bold text-slate-800">News Section Visibility</h3>
              <p className="text-sm text-slate-600">
                {sectionEnabled ? "Section is VISIBLE on the website" : "Section is HIDDEN from the website"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${sectionEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
              {sectionEnabled ? "VISIBLE" : "HIDDEN"}
            </span>
            <Switch
              checked={sectionEnabled}
              onCheckedChange={toggleSection}
              disabled={toggling}
              data-testid="toggle-news-visibility"
            />
          </div>
        </div>
      </div>

      {/* Scraper Control Panel */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <RefreshCw className="w-5 h-5" /> News Scraper
            </h3>
            <p className="text-sm text-slate-500">
              {scraperStatus?.last_sync 
                ? `Last run: ${new Date(scraperStatus.last_sync).toLocaleString()}`
                : "No scraper runs yet"}
            </p>
            {scraperStatus?.scheduler_running && (
              <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                <Clock className="w-3 h-3" /> Auto-runs hourly
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${scraperStatus?.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
            <span className="text-sm text-slate-600">
              {scraperStatus?.scheduler_running ? "Running" : "Idle"}
            </span>
            <Button 
              onClick={refreshNews} 
              disabled={refreshing}
              className="ml-4 bg-[#2BAEE5] hover:bg-[#228BB7]"
              data-testid="refresh-news-btn"
            >
              {refreshing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Refreshing...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" /> Refresh Now</>
              )}
            </Button>
          </div>
        </div>
        {scraperStatus?.article_counts && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xl font-bold text-[#0F172A]">{scraperStatus.article_counts.men || 0}</div>
              <div className="text-xs text-slate-500">Men's News</div>
            </div>
            <div>
              <div className="text-xl font-bold text-purple-600">{scraperStatus.article_counts.women || 0}</div>
              <div className="text-xs text-slate-500">Women's News</div>
            </div>
            <div>
              <div className="text-xl font-bold text-blue-600">{scraperStatus.article_counts.latest || 0}</div>
              <div className="text-xs text-slate-500">Latest (24h)</div>
            </div>
            <div>
              <div className="text-xl font-bold text-green-600">{socialAccounts.filter(s => s.is_active).length}</div>
              <div className="text-xs text-slate-500">Social Accounts</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="articles">Articles ({articles.length})</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-blog-tab">Blog ({blogPosts.length})</TabsTrigger>
          <TabsTrigger value="social">Social ({socialAccounts.length})</TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles">
          <div className="flex gap-3 mb-4 flex-wrap justify-between">
            <div className="flex gap-3">
              <Select value={filter.team} onValueChange={(v) => setFilter({...filter, team: v})}>
                <SelectTrigger className="w-32"><SelectValue placeholder="Team" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  <SelectItem value="men">Men's</SelectItem>
                  <SelectItem value="women">Women's</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filter.category} onValueChange={(v) => setFilter({...filter, category: v})}>
                <SelectTrigger className="w-36"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="latest">Latest</SelectItem>
                  <SelectItem value="review">Match Reviews</SelectItem>
                  <SelectItem value="social">Social</SelectItem>
                  <SelectItem value="transfer">Transfers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => { setEditingArticle({...emptyArticle}); setDialogOpen(true); }}
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
              data-testid="add-article-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Article
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[400px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead>Headline</TableHead>
                    <TableHead className="w-20">Team</TableHead>
                    <TableHead className="w-24">Category</TableHead>
                    <TableHead className="w-24">Source</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredArticles.slice(0, 50).map(article => (
                    <TableRow key={article.id}>
                      <TableCell>
                        <div className="max-w-md truncate font-medium">{article.headline}</div>
                        <div className="text-xs text-slate-500 truncate max-w-md">{article.summary?.substring(0, 80)}...</div>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${article.team === "men" ? "bg-[#7C2C3B] text-white" : "bg-purple-600 text-white"}`}>
                          {article.team === "men" ? "Men" : "Women"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded bg-slate-100 capitalize">
                          {article.category}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {article.source_url ? (
                          <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                            {article.source || "Link"} <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          article.source || "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <ViewLiveLink slug={article.slug} path="/news" testId={`article-${article.id}`} />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 w-7 p-0"
                            onClick={() => { setEditingArticle({...article}); setDialogOpen(true); }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                            onClick={() => deleteArticle(article.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            Showing {Math.min(50, filteredArticles.length)} of {filteredArticles.length} articles
          </p>
        </TabsContent>

        {/* Blog Posts Tab */}
        <TabsContent value="blog">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => { setEditingBlog({...emptyBlog}); setBlogDialogOpen(true); }}
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
              data-testid="add-blog-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Blog Post
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto max-h-[400px]">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-100 z-10">
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead className="w-24">Author</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    <TableHead className="w-28">Published</TableHead>
                    <TableHead className="w-28">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blogPosts.map(blog => (
                    <TableRow key={blog.id}>
                      <TableCell>
                        <div className="max-w-md truncate font-medium">{blog.title}</div>
                        <div className="text-xs text-slate-500 truncate max-w-md">{blog.content?.substring(0, 80)}...</div>
                      </TableCell>
                      <TableCell className="text-sm">{blog.author || "-"}</TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${blog.is_published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                          {blog.is_published ? "Published" : "Draft"}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {blog.published_at ? new Date(blog.published_at).toLocaleDateString('en-GB') : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <ViewLiveLink slug={blog.slug} path="/news" testId={`blog-${blog.id}`} />
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0"
                            onClick={() => {
                              setEditingBlog({
                                ...blog,
                                images: blog.images || ["", ""],
                                links: blog.links || [{ url: "", text: "" }, { url: "", text: "" }]
                              });
                              setBlogDialogOpen(true);
                            }}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                            onClick={() => deleteBlog(blog.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {blogPosts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No blog posts yet. Create your first ALLWHUFC News post.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* Social Accounts Tab */}
        <TabsContent value="social">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => { setEditingSocial({...emptySocial}); setSocialDialogOpen(true); }}
              className="bg-[#7C2C3B] hover:bg-[#63232F]"
              data-testid="add-social-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Social Account
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Platform</TableHead>
                  <TableHead>Handle</TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {socialAccounts.map(account => (
                  <TableRow key={account.id} className={!account.is_active ? "bg-slate-50 opacity-60" : ""}>
                    <TableCell>
                      <div className={`w-8 h-8 rounded flex items-center justify-center ${
                        account.platform === "twitter" ? "bg-blue-500 text-white" :
                        account.platform === "instagram" ? "bg-pink-500 text-white" :
                        "bg-slate-500 text-white"
                      }`}>
                        {getPlatformIcon(account.platform)}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">@{account.handle}</TableCell>
                    <TableCell>{account.display_name}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded ${account.is_active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>
                        {account.is_active ? "Active" : "Inactive"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 w-7 p-0"
                          onClick={() => { setEditingSocial({...account}); setSocialDialogOpen(true); }}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => deleteSocialAccount(account.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {socialAccounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                      No social accounts configured. Add accounts to track social headlines.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Article Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingArticle?.id ? "EDIT ARTICLE" : "ADD NEW ARTICLE"}
            </DialogTitle>
          </DialogHeader>
          
          {editingArticle && (
            <div className="grid gap-4 py-4">
              <div>
                <Label>Headline</Label>
                <Input 
                  value={editingArticle.headline} 
                  onChange={(e) => setEditingArticle({...editingArticle, headline: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Summary (50 words)</Label>
                <Textarea 
                  value={editingArticle.summary} 
                  onChange={(e) => setEditingArticle({...editingArticle, summary: e.target.value})}
                  rows={3}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Long Summary — &ldquo;The Story So Far&rdquo; (LLM-generated, ~100 words)</Label>
                  {editingArticle.id && (
                    <LlmRegenerateButton
                      collection="news"
                      docId={editingArticle.id}
                      onText={(text) =>
                        setEditingArticle({ ...editingArticle, long_summary: text })
                      }
                      testId="news-regenerate-long-summary"
                    />
                  )}
                </div>
                <Textarea
                  value={editingArticle.long_summary || ""}
                  onChange={(e) =>
                    setEditingArticle({ ...editingArticle, long_summary: e.target.value })
                  }
                  rows={6}
                  placeholder="This is the 100-word factual news brief shown on /news/:slug under the summary. Edit freely — your changes will not be overwritten by the LLM."
                  data-testid="news-long-summary-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {(editingArticle.long_summary || "").split(/\s+/).filter(Boolean).length} words · Google&apos;s Soft 404 detector expects ≥60 unique words per article page.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Team</Label>
                  <Select value={editingArticle.team} onValueChange={(v) => setEditingArticle({...editingArticle, team: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="men">Men's</SelectItem>
                      <SelectItem value="women">Women's</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={editingArticle.category} onValueChange={(v) => setEditingArticle({...editingArticle, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="latest">Latest</SelectItem>
                      <SelectItem value="review">Match Review</SelectItem>
                      <SelectItem value="social">Social</SelectItem>
                      <SelectItem value="transfer">Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Source</Label>
                  <Input 
                    value={editingArticle.source} 
                    onChange={(e) => setEditingArticle({...editingArticle, source: e.target.value})}
                    placeholder="e.g. BBC Sport"
                  />
                </div>
                <div>
                  <Label>Source URL</Label>
                  <Input 
                    value={editingArticle.source_url} 
                    onChange={(e) => setEditingArticle({...editingArticle, source_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div>
                <Label>Image (optional)</Label>
                <ImageUpload
                  value={editingArticle.image_url}
                  onChange={(url) => setEditingArticle({...editingArticle, image_url: url})}
                  label="Upload article image"
                />
              </div>

              {/* SEO metadata editor — cleared server-side when headline
                  or long_summary change, so this panel will auto-show a
                  "stale" banner until admin regenerates. */}
              {editingArticle.id && (
                <SeoMetaEditor collection="news" docId={editingArticle.id} />
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveArticle} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-article-btn">
              {editingArticle?.id ? "Update" : "Create"} Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Social Account Dialog */}
      <Dialog open={socialDialogOpen} onOpenChange={setSocialDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingSocial?.id ? "EDIT SOCIAL ACCOUNT" : "ADD SOCIAL ACCOUNT"}
            </DialogTitle>
          </DialogHeader>
          
          {editingSocial && (
            <div className="grid gap-4 py-4">
              <div>
                <Label>Platform</Label>
                <Select value={editingSocial.platform} onValueChange={(v) => setEditingSocial({...editingSocial, platform: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Handle (without @)</Label>
                <Input 
                  value={editingSocial.handle} 
                  onChange={(e) => setEditingSocial({...editingSocial, handle: e.target.value})}
                  placeholder="e.g. WestHam"
                />
              </div>
              
              <div>
                <Label>Display Name</Label>
                <Input 
                  value={editingSocial.display_name} 
                  onChange={(e) => setEditingSocial({...editingSocial, display_name: e.target.value})}
                  placeholder="e.g. West Ham United"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingSocial.is_active}
                  onCheckedChange={(v) => setEditingSocial({...editingSocial, is_active: v})}
                />
                <Label className="mb-0">Active (include in scraping)</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSocialDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveSocialAccount} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-social-btn">
              {editingSocial?.id ? "Update" : "Add"} Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Blog Post Dialog */}
      <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingBlog?.id ? "EDIT BLOG POST" : "NEW BLOG POST"}
            </DialogTitle>
          </DialogHeader>
          
          {editingBlog && (
            <div className="grid gap-4 py-4">
              <div>
                <Label>Title</Label>
                <Input 
                  value={editingBlog.title}
                  onChange={(e) => setEditingBlog({...editingBlog, title: e.target.value})}
                  placeholder="Blog post title"
                  data-testid="blog-title-input"
                />
              </div>
              
              <div>
                <Label>Content (~300 words)</Label>
                <Textarea 
                  value={editingBlog.content}
                  onChange={(e) => setEditingBlog({...editingBlog, content: e.target.value})}
                  rows={10}
                  placeholder="Write your blog post content here..."
                  data-testid="blog-content-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {editingBlog.content.split(/\s+/).filter(w => w).length} words
                </p>
              </div>

              <div>
                <Label>Author</Label>
                <Input 
                  value={editingBlog.author}
                  onChange={(e) => setEditingBlog({...editingBlog, author: e.target.value})}
                  placeholder="Author name (default: ALLWHUFC)"
                  data-testid="blog-author-input"
                />
              </div>

              <div>
                <Label>Image 1 (optional)</Label>
                <ImageUpload
                  value={editingBlog.images[0] || ""}
                  onChange={(url) => {
                    const imgs = [...editingBlog.images];
                    imgs[0] = url;
                    setEditingBlog({...editingBlog, images: imgs});
                  }}
                  label="Upload Image 1"
                />
              </div>
              <div>
                <Label>Image 2 (optional)</Label>
                <ImageUpload
                  value={editingBlog.images[1] || ""}
                  onChange={(url) => {
                    const imgs = [...editingBlog.images];
                    imgs[1] = url;
                    setEditingBlog({...editingBlog, images: imgs});
                  }}
                  label="Upload Image 2"
                />
              </div>

              <div className="border-t pt-3">
                <Label className="mb-2 block">Link 1 (optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    value={editingBlog.links[0]?.url || ""}
                    onChange={(e) => {
                      const links = [...editingBlog.links];
                      links[0] = {...links[0], url: e.target.value};
                      setEditingBlog({...editingBlog, links});
                    }}
                    placeholder="URL"
                  />
                  <Input 
                    value={editingBlog.links[0]?.text || ""}
                    onChange={(e) => {
                      const links = [...editingBlog.links];
                      links[0] = {...links[0], text: e.target.value};
                      setEditingBlog({...editingBlog, links});
                    }}
                    placeholder="Link text"
                  />
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Link 2 (optional)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input 
                    value={editingBlog.links[1]?.url || ""}
                    onChange={(e) => {
                      const links = [...editingBlog.links];
                      links[1] = {...links[1], url: e.target.value};
                      setEditingBlog({...editingBlog, links});
                    }}
                    placeholder="URL"
                  />
                  <Input 
                    value={editingBlog.links[1]?.text || ""}
                    onChange={(e) => {
                      const links = [...editingBlog.links];
                      links[1] = {...links[1], text: e.target.value};
                      setEditingBlog({...editingBlog, links});
                    }}
                    placeholder="Link text"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={editingBlog.is_published}
                  onCheckedChange={(v) => setEditingBlog({...editingBlog, is_published: v})}
                />
                <Label className="mb-0">Published</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlogDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveBlog} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-blog-btn">
              {editingBlog?.id ? "Update" : "Publish"} Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsAdmin;
